import type { InitOptions } from 'i18next';
import { canonicalizeLanguage } from './applyClosestSiblingResolver';
import { SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE } from './i18n.constants';

export { LANGUAGES, DEFAULT_LANGUAGE } from './i18n.constants';

// Static imports to avoid webpack warning and bundle bloat.
//
// Keyed by the BASE language code (`'es'`, not `'es-ES'`) because the consumer
// hook `useReactDatePickersLocale` indexes this map with `locale.language.split('-')[0]`.
// `date-fns/locale/es` is itself a base-language module — region-specific date-fns
// variants exist but are not loaded here; the base locale is a good default for
// every region of a language.
export const reactDatepickerLocaleImports: Record<string, () => Promise<{ default: Locale }>> = {
  es: () => import('date-fns/locale/es'),
  de: () => import('date-fns/locale/de'),
  fr: () => import('date-fns/locale/fr'),
  it: () => import('date-fns/locale/it'),
  ja: () => import('date-fns/locale/ja'),
};

export const getInitOptions = (overrides: Readonly<Partial<InitOptions>> = {}): InitOptions => ({
  // Load every language in the resolved chain, not just the active one. Combined with
  // explicit-only supportedLngs below, the chain for an exact match like 'de-DE' is
  // ['de-DE', 'en-US'] (bare 'de' is filtered out — it's not in supportedLngs). For a
  // regional variant like 'de-AT', applyClosestSiblingResolver injects 'de-DE' so the
  // chain becomes ['de-AT', 'de-DE', 'en-US'] and the sibling bundle is preloaded
  // alongside the fallback.
  load: 'all',

  // Explicit-only matching. Non-canonical region variants (de-AT, fr-CA) and
  // bare base codes (de, es) are handled by applyClosestSiblingResolver rather
  // than by `nonExplicitSupportedLngs`, which has the wrong shape for our needs
  // (it strips region from inputs before lookup, which would reject every
  // explicit BCP 47 code in SUPPORTED_LANGUAGE_CODES).
  nonExplicitSupportedLngs: false,

  preload: [DEFAULT_LANGUAGE],
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGE_CODES,

  returnEmptyString: false,
  cleanCode: true,

  interpolation: {
    escapeValue: false,
  },

  partialBundledLanguages: true,

  // i18next's LanguageDetector defaults to lookupQuerystring='lng', which collides
  // with the hotel/accommodations search 'lng' longitude URL param. Use 'l' (already
  // recognized in LANGUAGE_PARAMS) instead. See JW-47.
  //
  // `convertDetectedLanguage` runs against each detected code BEFORE the
  // supportedLngs filter. Canonicalizing here lets non-canonical Accept-Language
  // values (de-AT, fr-CA, bare de/es/fr/it/ja) pass the filter — otherwise the
  // i18next-http-middleware detector rejects them and falls through to en-US
  // before `toResolveHierarchy` (and the closest-sibling resolver wrap) ever
  // runs. See PRNT-4468 spec §5.
  detection: {
    lookupQuerystring: 'l',
    convertDetectedLanguage: canonicalizeLanguage,
  },

  react: {
    useSuspense: true,
    wait: !process?.release,
  },
  postProcess: [`reactPostprocessor`],
  ...overrides,
});

type ParsedFormat = {
  dateFormat: string[];
  dateInputMask: string[];
};

export const getDateFormatString = (locale: string) => {
  const formatObj = new Intl.DateTimeFormat(locale).formatToParts(new Date());
  const parsed = formatObj.reduce(
    (acc, obj) => {
      switch (obj.type) {
        case 'day':
          acc.dateFormat.push('dd');
          acc.dateInputMask.push('99');
          break;
        case 'month':
          acc.dateFormat.push('MM');
          acc.dateInputMask.push('99');
          break;
        case 'year':
          acc.dateFormat.push('yyyy');
          acc.dateInputMask.push('9999');
          break;
      }
      return acc;
    },
    { dateFormat: [], dateInputMask: [] } as ParsedFormat,
  );
  return {
    dateFormat: parsed.dateFormat.join('/'),
    dateInputMask: parsed.dateInputMask.join('/'),
  };
};

/**
 * Determines whether a given BCP 47 language code is accepted by joy-web's i18n.
 *
 * Accepts:
 *   - exact match in SUPPORTED_LANGUAGE_CODES (e.g. 'en-US', 'de-DE')
 *   - any input whose base language shares a prefix with a supported code —
 *     covers both region variants ('en-CA', 'de-AT', 'fr-CA') and bare base
 *     codes ('de', 'es', 'fr'). 'en-CA' is admitted because 'en-US', 'en-GB',
 *     and 'en-AU' all start with 'en-'; bare 'de' is admitted because 'de-DE'
 *     starts with 'de-'.
 *
 * Liberal interpretation per PRNT-4468 — telemetry / i18n.language preserves
 * the user's reported region (e.g. 'en-CA'); content resolution uses the
 * canonical sibling injected by applyClosestSiblingResolver.
 */
export const languageSupported = (extendedLanguageCode: string): boolean => {
  const [rawLanguage, rawRegion] = extendedLanguageCode.split('-');
  const language = rawLanguage?.toLowerCase();
  const region = rawRegion?.toUpperCase();
  const normalized = region ? `${language}-${region}` : language;
  if (!language) return false;
  if (SUPPORTED_LANGUAGE_CODES.includes(normalized)) return true;
  return SUPPORTED_LANGUAGE_CODES.some(code => code.toLowerCase().startsWith(`${language}-`));
};
